<template>
  <VModal :showing="showing" container-classes="max-w-192" data-testid="model-selector" @update:showing="$emit('close')">
    <template #header>
      <VModalHeader>
        <template #title>Choose any number of slides from {{ selectedDeck.title }}</template>
        <template #sub-title>Create 3D Slide</template>
      </VModalHeader>
    </template>

    <template #default>
      <div v-if="hasNoSlides" class="max-w-xs mx-auto mt-10 mb-16 font-light text-center text-gray-600" data-testid="empty-message">
        <p class="mb-4 text-xl">This deck has no 3D slides.</p>
        <p class="mb-0">Please contact your VRIFY support team to track the completion of your Project</p>
      </div>

      <VSearchableThumbnailList
        v-else
        ref="thumbnailListRef"
        v-model:search="search"
        :auto-load="true"
        :options="modelList as unknown as Option[]"
        :loading="loading"
        :count="count"
        placeholder="Search by slide name, model ID or model name"
        data-testid="thumbnail-list"
        @selected="toggleSelected($event.id)"
        @load-more="scroll()"
      >
        <template #item="{ option }">
          <EditorDeckThumbnailTile
            readonly
            variant="search"
            :item="(option as unknown as ThreeDSlide)"
            :selected="isItemSelected(option as unknown as ThreeDSlide)"
            placeholder="Slide Name"
            :data-testid="`tile-${(option as unknown as ThreeDSlide).id}`"
            class="slide"
          >
            <template #top>
              <div
                class="flex items-center mt-1 mb-2 text-xs text-gray-800 uppercase"
                :data-testid="`model-id-${(option as unknown as ThreeDSlide).model_id}`"
              >
                <CubeIcon class="flex-shrink-0 w-5 h-5 mr-1" />
                <span class="flex-grow truncate">
                  {{ (option as unknown as ThreeDSlide).model_id }} &ndash;
                  {{ (option as unknown as ThreeDSlide).model_camera && (option as unknown as ThreeDSlide).model_camera!.model_title }}
                </span>
              </div>
            </template>

            <template #default>
              <div
                v-if="(option as unknown as ThreeDSlide).model_camera && (option as unknown as ThreeDSlide).model_camera!.preview"
                class="flex items-center justify-center w-full h-full bg-gray-700"
              >
                <img :src="(option as unknown as ThreeDSlide).model_camera!.preview" draggable="false" />
              </div>
              <PlaceholderDeck v-else />
            </template>
          </EditorDeckThumbnailTile>
        </template>
      </VSearchableThumbnailList>
    </template>

    <template #footer>
      <div class="flex justify-end gap-2">
        <VButton @click="$emit('back')">Back</VButton>
        <VButton
          class="w-56"
          variant="copper"
          data-testid="copy-slides"
          :disabled="!selectedItems.length || isCopying"
          @click="copySelectedSlides"
        >
          Copy {{ `${selectedItems.length || ''} ${pluralize(selectedItems.length, 'Slide', 'Slides')}` }}
        </VButton>
      </div>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { ref, computed } from 'vue'
import { CubeIcon } from '@heroicons/vue/24/solid'

import type { Model3D } from '@/entities/model3d'
import type { Deck, ThreeDSlide } from '@/entities/deck'
import type { QueryParameters } from '@/composables/useIndexSearch'
import type { MultiSelectItem } from '@/composables/useMultipleSlidesSelect'
import type { Option } from '@/components/VSearchableThumbnailList.vue'
import { pluralize } from '@/lib/pluralize'
import { formatDateTimeMed } from '@/lib/datetime-format-utils'
import PlaceholderDeck from '@/components/icons/deck-placeholder.svg'

interface ModelQuery extends QueryParameters {
  sortKey: 'name' | 'updated_at'
  sortDir: '-' | ''
}
interface ApiQuery {
  query: string | null
  limit: number
}

const props = defineProps({
  showing: {
    type: Boolean,
    default: false,
  },
  selectedDeck: {
    type: Object as PropType<Deck>,
    required: true,
  },
  currentModel: {
    type: Object as PropType<Model3D>,
    default: null,
  },
})

const emit = defineEmits(['back', 'close', 'copy-slides'])

const thumbnailListRef = ref<any | undefined>()
const modelList = computed(() => results.value ?? [])
const selected = ref(false)
const isCopying = ref(false)
const hasNoSlides = computed(() => !loading.value && !search.value?.trim() && !results.value.length)

const {
  results,
  query: search,
  count,
  continuation,
  loading,
} = useIndexSearch<ThreeDSlide, ModelQuery, ApiQuery>({
  initParams: { limit: 12, sortDir: '-', sortKey: 'updated_at' },
  endpoint: () => `v2/decks/${props.selectedDeck.id}/slides?type=M`,
  finalParameters: ({ query, limit }) => ({
    query,
    limit,
    include: ['model_camera'],
  }),
})

const { selectedSlideIds: selectedItems, onClick: toggleSelected } = useMultipleSlidesSelect({
  slides: computed(() => modelList.value.map(x => ({ id: x.id })) as readonly MultiSelectItem[]),
  currentSlideId: computed(() => Infinity),
  setCurrentSlideIndex: () => {},
  suppressReset: true,
})

function isItemSelected(item: MultiSelectItem): boolean {
  return selectedItems.value.some(id => id === item.id)
}

function copySelectedSlides() {
  // To keep the order in which the slides were selected
  const newSlides = selectedItems.value.reduce((acc: ThreeDSlide[], val: number) => {
    const item = modelList.value.find(x => x.id === val)
    return !item ? acc : acc.concat(item)
  }, [])

  emit('copy-slides', newSlides)
  isCopying.value = true
}

async function scroll() {
  if (!loading.value && count.value > results.value.length) {
    continuation.value++
  }
}
</script>
